import type { Localized, LocalizedList } from "@/i18n/types";

/**
 * Only prose is localized. Names, dates, URLs and technology labels are the
 * same in both languages, so duplicating them would just create drift.
 */
export const profile = {
  name: "Daniel Franco",
  role: "Lead / Staff Engineer",
  disciplines: ["Web", "Mobile", "Backend", "DevOps"],
  domain: "franco.engineer",
  email: "dafrannco@gmail.com",
  linkedin: "https://www.linkedin.com/in/daniel-franco",
  linkedinLabel: "linkedin.com/in/daniel-franco",
  resumePath: "/daniel-franco-resume.pdf",

  // The one a recruiter wants from the hero. Must appear in `github` below —
  // asserted in src/test/content.test.tsx so the two cannot drift apart.
  githubPrimary: "https://github.com/dcfranco",

  github: [
    { label: "Personal", handle: "dcfranco", url: "https://github.com/dcfranco" },
    { label: "HookSys", handle: "HookSys", url: "https://github.com/HookSys" },
    { label: "Francos Tech", handle: "francos-tech", url: "https://github.com/orgs/francos-tech" },
  ],

  location: {
    en: "Brazil",
    pt: "Brasil",
  } satisfies Localized,

  availability: {
    en: "Remote",
    pt: "Remoto",
  } satisfies Localized,

  intro: {
    en: "I lead end-to-end delivery of high-reliability web and mobile systems — from the React client, through the Node and Java services, down to the Terraform that runs them. Most of the last decade has been in domains where being wrong is expensive: payments, regulated lottery and betting, healthcare, and hardware on a shop floor.",
    pt: "Lidero a entrega ponta a ponta de sistemas web e mobile de alta confiabilidade — do cliente React, passando pelos serviços em Node e Java, até o Terraform que os sustenta. Boa parte da última década foi em domínios onde errar sai caro: pagamentos, loteria e apostas reguladas, saúde e hardware em chão de fábrica.",
  } satisfies Localized,

  // Rendered after "What I'm known for: " / "Sou conhecido por: ". Portuguese
  // takes lowercase after a colon, English tolerates the capital.
  hook: {
    en: "Finding the root cause fast, in whichever layer it actually lives.",
    pt: "encontrar a causa raiz rápido, na camada em que ela realmente está.",
  } satisfies Localized,

  about: {
    en: [
      "I started in incident triage at T-Systems, working out why things broke for Volkswagen before I ever shipped a feature of my own. That order of operations stuck — I learned systems by debugging them.",
      "Since then I've led frontend, backend and infrastructure work across healthcare, retail POS, insurance, crypto and regulated gaming, most recently at Lottery Now, where the software handles real money under real regulators.",
      "I'm most useful to teams that need someone who won't stop at a layer boundary. The bug reports that bounce between frontend, API and infra tend to land on me.",
    ],
    pt: [
      "Comecei na triagem de incidentes da T-Systems, descobrindo por que as coisas quebravam na Volkswagen antes mesmo de entregar minha primeira feature. Essa ordem ficou: aprendi os sistemas depurando-os.",
      "Desde então liderei frentes de frontend, backend e infraestrutura em saúde, PDV de varejo, seguros, cripto e jogos regulados — mais recentemente na Lottery Now, onde o software movimenta dinheiro real sob reguladores reais.",
      "Sou mais útil em times que precisam de alguém que não pare na fronteira entre camadas. Os bugs que ficam quicando entre frontend, API e infra costumam parar comigo.",
    ],
  } satisfies LocalizedList,

  cta: {
    en: "Open to Lead / Staff engineering roles — remote.",
    pt: "Aberto a posições de Lead / Staff Engineer — remoto.",
  } satisfies Localized,

  stats: [
    {
      value: { en: "14 yrs", pt: "14 anos" },
      label: { en: "shipping", pt: "entregando" },
    },
    {
      value: { en: "8", pt: "8" },
      label: { en: "companies", pt: "empresas" },
    },
    {
      value: { en: "20+", pt: "20+" },
      label: { en: "products", pt: "produtos" },
    },
    {
      value: { en: "client → API → infra", pt: "cliente → API → infra" },
      label: { en: "scope", pt: "escopo" },
    },
  ],

  languages: [
    {
      name: { en: "English", pt: "Inglês" },
      level: { en: "Professional proficiency", pt: "Proficiência profissional" },
    },
    {
      name: { en: "Portuguese", pt: "Português" },
      level: { en: "Native proficiency", pt: "Proficiência nativa" },
    },
  ],

  education: [
    {
      // The Brazilian course is "Análise e Desenvolvimento de Sistemas" — the
      // pt string is the real name, not a translation of the English gloss.
      degree: {
        en: "Analysis and Development of Software",
        pt: "Análise e Desenvolvimento de Sistemas",
      },
      school: "IFC — Instituto Federal Catarinense",
      period: "2014 — 2018",
    },
    {
      degree: {
        en: "Technical Course, Software Developer",
        pt: "Curso Técnico em Desenvolvimento de Software",
      },
      school: "CEDUP — Centro de Educação Profissional Hermann Hering",
      period: "2010 — 2013",
    },
    {
      degree: {
        en: "Technical Course, Java Software Developer",
        pt: "Curso Técnico em Desenvolvimento Java",
      },
      school: "CEDUP — Centro de Educação Profissional Hermann Hering",
      period: "2011 — 2012",
    },
  ],
} as const;
